import { Element, Node, Path } from 'slate'
import type { TunaEditor } from '@tunasong/plugin-lib'

type Slate = Parameters<typeof Node.get>[0]

/**
 * The node a "Discard sketch" removes, and where it lived.
 *
 * Generated media is inserted wrapped in a `ref` whose only child is the media element
 * (`insertWithPlaceholder`), so the block the user sees as "the sketch" is the wrapper, not the
 * element that renders the rail. Removing only the inner element leaves an empty wrapper that the
 * editor normalizes away — and Undo, holding the inner path, then inserts into a parent that no
 * longer exists and throws.
 *
 * A ref that holds anything besides the media is not the sketch's own wrapper, so only the media
 * goes.
 */
export const resolveDiscardTarget = (editor: TunaEditor, path: Path): { at: Path; node: Node } => {
  const root = editor as unknown as Slate
  if (path.length > 1) {
    const parentPath = Path.parent(path)
    const parent = Node.get(root, parentPath)
    if (Element.isElement(parent) && (parent as { type?: string }).type === 'ref' && parent.children.length === 1) {
      return { at: parentPath, node: parent }
    }
  }
  return { at: path, node: Node.get(root, path) }
}

/**
 * Where Undo re-inserts a discarded sketch.
 *
 * Undo is a fresh transform, not a history pop, so the document can change while the snackbar is
 * up — in a shared song a bandmate may delete blocks. Clamp the index to the end of its parent so a
 * stale path restores the sketch at the end rather than throwing. If the parent itself is gone,
 * fall back to the end of the document.
 */
export const resolveRestorePath = (editor: TunaEditor, at: Path): Path => {
  const root = editor as unknown as Slate
  const parentPath = Path.parent(at)
  if (!Node.has(root, parentPath)) {
    return [editor.children.length]
  }
  const { children } = Node.get(root, parentPath) as { children: Node[] }
  return [...parentPath, Math.min(at[at.length - 1] ?? 0, children.length)]
}
